import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFasletOrderTracking } from '@faslet/react-widget';
import { ACCESSORY_TYPE_EN } from '@yme/common/selectors';
import { FASLET_ORDER_TRACKING_STATE, FASLET_SHOPPING_ID, } from '@yme/pdp-components/Faslet/constants';
import { getFasletShoppingId } from '@yme/pdp-components/Faslet/selectors';
import { getAttribute, getProductDetailsData } from '@yme/selectors/products';
import { getMasterVariantSku } from '@yme/utils/product';
export const useOrderTrackingData = ({ cartEntries, orderNumber, paymentStatus, }) => {
    const handleClearFasletStorage = () => {
        localStorage.removeItem(FASLET_ORDER_TRACKING_STATE);
    };
    const shoppingId = useSelector(getFasletShoppingId);
    const localFasletShoppingId = (shoppingId || localStorage.getItem(FASLET_SHOPPING_ID)) ?? '';
    const productsInOrder = cartEntries?.map((entry) => ({
        productId: entry.product.slug,
        variantId: entry.product.variants[0]?.sku,
        productName: entry.product.name,
        variantName: entry.product.variants[0].attributes.find((attr) => attr.name === 'size')?.value,
        priceTimesQuantity: entry.totalPrice.amount,
        quantity: entry.quantity,
        sku: entry.product.variants[0]?.sku,
    }));
    useFasletOrderTracking(localFasletShoppingId, orderNumber, paymentStatus, productsInOrder);
    return {
        handleClearFasletStorage,
    };
};
export function useProductVariantSelector({ onSelectSize, products, shopId, }) {
    const getSizeBySku = useCallback((sku) => products?.find((product) => product.sku === sku)?.size, [products]);
    const getSkuBySize = useCallback((size) => products?.find((product) => product.size === size)?.sku, [products]);
    const selectSizeByDomResult = useCallback((currentSize) => {
        if (!products?.length)
            return;
        const shopElement = document.querySelector(`[shop-id='${shopId}']`);
        if (!shopElement?.shadowRoot) {
            console.warn('Faslet widget or shadow root not found.');
            return;
        }
        const sizeLabelContent = shopElement.shadowRoot.querySelector('.result-label-content');
        if (!sizeLabelContent) {
            console.warn('Expected element within shadow root not found.');
            return;
        }
        const size = sizeLabelContent.textContent;
        const matchedProductSize = products?.find((product) => product.size === size)?.size;
        if (matchedProductSize && matchedProductSize !== currentSize) {
            onSelectSize?.(matchedProductSize);
        }
    }, [onSelectSize, products, shopId]);
    return {
        selectSizeByDomResult,
        getSizeBySku,
        getSkuBySize,
    };
}
export const useEnhancedAdobeAnalytics = (analyticsHandler) => {
    const productDetailsData = useSelector(getProductDetailsData);
    const accessoryTypeEn = useSelector(getAttribute(ACCESSORY_TYPE_EN));
    const { name, productNameEn = '', type = '', variants, slug } = productDetailsData || {};
    const masterVariantSku = getMasterVariantSku(variants);
    const triggerAnalytics = useCallback((eventName, productSku) => {
        analyticsHandler(eventName, {
            sku: productSku,
            productId: slug,
            name: name || productNameEn,
            type,
            masterSkuCode: masterVariantSku,
            category: accessoryTypeEn,
        });
    }, [accessoryTypeEn, analyticsHandler, masterVariantSku, name, productNameEn, slug, type]);
    return {
        triggerAnalytics,
    };
};
